import axios from "../api/axiosInstance";

export function readRmsSessionUser() {
  try {
    return JSON.parse(localStorage.getItem("palantir_user") || "{}");
  } catch (_error) {
    return {};
  }
}

export function resolveBusinessUnitId(searchParams) {
  const sessionUser = readRmsSessionUser();
  const raw =
    searchParams?.get?.("business_unit_id") ||
    sessionUser?.active_business_unit_id ||
    sessionUser?.business_unit_id ||
    "1";
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

export function buildBusinessUnitPath(path, businessUnitId) {
  return `${path}?business_unit_id=${encodeURIComponent(String(businessUnitId))}`;
}

export async function fetchRmsOutputs(endpoint, params = {}) {
  const { data } = await axios.get(`/api/rms/outputs/${endpoint}`, { params });
  return data;
}

const RMS_RECOMMENDATION_WORKFLOW_ACTIONS = new Set([
  "approve",
  "apply",
  "rollback",
]);

function assertRmsRecommendationWorkflowAction(action) {
  const normalized = String(action || "").trim().toLowerCase();
  if (!RMS_RECOMMENDATION_WORKFLOW_ACTIONS.has(normalized)) {
    throw new Error("Unsupported RMS recommendation workflow action.");
  }
  return normalized;
}

export async function fetchRmsRecommendationWorkflow({
  businessUnitId,
  pipelineRunId,
}) {
  const { data } = await axios.get("/api/rms/recommendation-workflow", {
    params: {
      business_unit_id: businessUnitId,
      pipeline_run_id: pipelineRunId,
    },
  });
  return data;
}

export async function previewRmsRecommendationWorkflowAction(action, payload) {
  const normalizedAction = assertRmsRecommendationWorkflowAction(action);
  const { data } = await axios.post(
    `/api/rms/recommendation-workflow/${normalizedAction}/preview`,
    payload
  );
  return data;
}

export async function executeRmsRecommendationWorkflowAction(action, payload) {
  const normalizedAction = assertRmsRecommendationWorkflowAction(action);
  const { data } = await axios.post(
    `/api/rms/recommendation-workflow/${normalizedAction}/execute`,
    payload
  );
  return data;
}

export async function fetchRmsConfigMeta() {
  const { data } = await axios.get("/api/rms/config/meta");
  return data;
}

export async function saveRmsCategoryPriceLadder(payload) {
  const { data } = await axios.put(
    "/api/rms/config/category-price-ladder",
    payload
  );
  return data;
}

export async function fetchRmsOccupancyPace() {
  const { data } = await axios.get("/api/rms/config/occupancy-pace");
  return data?.occupancy_pace || null;
}

export async function saveRmsOccupancyPace(payload) {
  const { data } = await axios.put("/api/rms/config/occupancy-pace", payload);
  return data?.occupancy_pace || null;
}

export async function fetchRmsPriceResponse() {
  const { data } = await axios.get("/api/rms/config/price-response");
  return data?.price_response || data || null;
}

export async function saveRmsPriceResponse(payload) {
  const { data } = await axios.put("/api/rms/config/price-response", payload);
  return data?.price_response || data || null;
}

export async function fetchRmsOrphanGapPolicy() {
  const { data } = await axios.get("/api/rms/config/orphan-gap-policy");
  return data?.orphan_gap_policy || null;
}

export async function saveRmsOrphanGapPolicy(payload) {
  const { data } = await axios.put(
    "/api/rms/config/orphan-gap-policy",
    payload
  );
  return data?.orphan_gap_policy || null;
}

export async function fetchRmsOrphanGapNearPolicy() {
  const { data } = await axios.get("/api/rms/config/orphan-gap-near-policy");
  return data?.orphan_gap_near_policy || null;
}

export async function saveRmsOrphanGapNearPolicy(payload) {
  const { data } = await axios.put(
    "/api/rms/config/orphan-gap-near-policy",
    payload
  );
  return data?.orphan_gap_near_policy || null;
}

export async function fetchRmsOrphanGapStrongPolicy() {
  const { data } = await axios.get(
    "/api/rms/config/orphan-gap-strong-policy"
  );
  return data?.orphan_gap_strong_policy || null;
}

export async function saveRmsOrphanGapStrongPolicy(payload) {
  const { data } = await axios.put(
    "/api/rms/config/orphan-gap-strong-policy",
    payload
  );
  return data?.orphan_gap_strong_policy || null;
}

export async function fetchRmsMarketBenchmarkPolicy() {
  const { data } = await axios.get(
    "/api/rms/config/market-benchmark-policy"
  );
  return data?.market_benchmark_policy || null;
}

export async function saveRmsMarketBenchmarkPolicy(payload) {
  const { data } = await axios.put(
    "/api/rms/config/market-benchmark-policy",
    payload
  );
  return data?.market_benchmark_policy || null;
}

export async function fetchRmsRateShopConfig(businessUnitId = resolveBusinessUnitId()) {
  const { data } = await axios.get("/api/ratemetrics/rateshop-configs", {
    params: { business_unit_id: businessUnitId },
  });
  const rows = Array.isArray(data) ? data : [];
  return rows[0] || null;
}

export async function fetchRmsCompetitorRoomTypesDistinct(params = {}) {
  const { data } = await axios.get("/api/ratemetrics/room-types/distinct", { params });
  return Array.isArray(data) ? data : [];
}

export async function fetchRmsQualitySettings() {
  const { data } = await axios.get("/api/rms/config/quality-settings");
  return data?.item || null;
}

export async function saveRmsQualitySettings(payload) {
  const { data } = await axios.put("/api/rms/config/quality-settings", payload);
  return data;
}

export async function fetchRmsConfigResource(resource) {
  const { data } = await axios.get(`/api/rms/config/${resource}`);
  return Array.isArray(data?.items) ? data.items : [];
}

export async function createRmsConfigResource(resource, payload) {
  const { data } = await axios.post(`/api/rms/config/${resource}`, payload);
  return data;
}

export async function updateRmsConfigResource(resource, id, payload) {
  const { data } = await axios.put(`/api/rms/config/${resource}/${id}`, payload);
  return data;
}

export async function deleteRmsConfigResource(resource, id) {
  const { data } = await axios.delete(`/api/rms/config/${resource}/${id}`);
  return data;
}

export async function fetchRmsMarketReferenceRoomRates(businessUnitId) {
  const { data } = await axios.get("/api/rms/market-pricing/room-rates", {
    params: { business_unit_id: businessUnitId },
  });
  return Array.isArray(data?.room_rates) ? data.room_rates : [];
}

export async function previewRmsMarketReference(payload) {
  const { data } = await axios.post("/api/rms/market-pricing/preview", payload);
  return data;
}

export async function createRmsMarketReference(payload) {
  const { data } = await axios.post("/api/rms/market-pricing/references", payload);
  return data;
}

export async function fetchRmsMarketReferences(businessUnitId, limit = 50) {
  const { data } = await axios.get("/api/rms/market-pricing/references", {
    params: { business_unit_id: businessUnitId, limit },
  });
  return Array.isArray(data?.references) ? data.references : [];
}

export async function fetchRmsMarketReference(referenceId, businessUnitId) {
  const { data } = await axios.get(
    `/api/rms/market-pricing/references/${encodeURIComponent(String(referenceId))}`,
    { params: { business_unit_id: businessUnitId } }
  );
  return data;
}

// Compatibility verifier only. New applications use the atomic RMS endpoint
// below; this function never writes PMS prices.
export async function confirmRmsMarketReferenceApplication(referenceId, businessUnitId) {
  const { data } = await axios.post(
    `/api/rms/market-pricing/references/${encodeURIComponent(
      String(referenceId)
    )}/confirm-application`,
    { business_unit_id: businessUnitId }
  );
  return data;
}

export async function applyRmsMarketReference(
  reference,
  { stayDates = [], onProgress } = {}
) {
  const selectedStayDates = [...new Set(stayDates)]
    .map((value) => String(value || "").slice(0, 10))
    .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))
    .sort();
  if (!selectedStayDates.length) {
    throw new Error("Select at least one date to approve.");
  }
  const approvalSnapshotHash = String(
    reference?.approval_snapshot_hash || ""
  ).trim();
  if (!/^[0-9a-f]{64}$/.test(approvalSnapshotHash)) {
    throw new Error("Refresh this proposal before approving its prices.");
  }
  const total = selectedStayDates.length;
  if (typeof onProgress === "function") {
    onProgress({ completed: 0, total, errors: 0 });
  }

  const { data } = await axios.post(
    `/api/rms/market-pricing/references/${encodeURIComponent(
      String(reference.id)
    )}/apply`,
    {
      business_unit_id: reference.business_unit_id,
      approval_snapshot_hash: approvalSnapshotHash,
      stay_dates: selectedStayDates,
    }
  );

  if (typeof onProgress === "function") {
    onProgress({ completed: total, total, errors: 0 });
  }

  return { reference: data, errors: [] };
}
